import React, { useState, useEffect, useCallback } from 'react';
import { metaPixel } from '../services/metaPixel';

const CONSENT_KEY = 'cookieConsent';
const CONSENT_VERSION = '1';

interface ConsentState {
    version: string;
    analytics: boolean;
    timestamp: number;
}

function applyGtagConsent(analytics: boolean) {
    if (typeof window !== 'undefined' && typeof (window as any).gtag === 'function') {
        (window as any).gtag('consent', 'update', {
            analytics_storage: analytics ? 'granted' : 'denied',
        });
    }
}

function applyMetaPixelConsent(analytics: boolean) {
    if (analytics) {
        metaPixel.grantConsent();
    } else {
        metaPixel.revokeConsent();
    }
}

function saveConsent(analytics: boolean) {
    const state: ConsentState = {
        version: CONSENT_VERSION,
        analytics,
        timestamp: Date.now(),
    };
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
    } catch (e) {}
    applyGtagConsent(analytics);
    applyMetaPixelConsent(analytics);
}

export function CookieConsentBanner() {
    const [visible, setVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);

    useEffect(() => {
        try {
            const saved = localStorage.getItem(CONSENT_KEY);
            if (saved) {
                const parsed: ConsentState = JSON.parse(saved);
                if (parsed.version === CONSENT_VERSION) return;
            }
        } catch (e) {}
        const timer = setTimeout(() => setVisible(true), 1500);
        return () => clearTimeout(timer);
    }, []);

    const acceptAll = useCallback(() => {
        saveConsent(true);
        setVisible(false);
    }, []);

    const acceptRequired = useCallback(() => {
        saveConsent(false);
        setVisible(false);
    }, []);

    if (!visible) return null;

    return (
        <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">

            {/* Tercihler paneli — yavaş açılıp kapanır */}
            <div
                className={`overflow-hidden transition-all duration-500 ease-in-out bg-white border-t border-glass-200 shadow-xl ${
                    showPreferences ? 'max-h-72 opacity-100' : 'max-h-0 opacity-0'
                }`}
            >
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 space-y-4">

                    {/* Zorunlu */}
                    <div className="flex items-start justify-between gap-6">
                        <div>
                            <p className="text-sm font-medium text-glass-800">Zorunlu Çerezler</p>
                            <p className="text-xs text-glass-500 mt-1 leading-relaxed">
                                Oturum güvenliği, Firebase kimlik doğrulaması ve temel platform
                                işlevleri için gereklidir. Her iki seçenekte de aktiftir.
                            </p>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-glass-400 whitespace-nowrap">Her zaman açık</span>
                            <div className="w-9 h-5 rounded-full bg-glass-200 relative cursor-not-allowed">
                                <div className="absolute right-0.5 top-0.5 w-4 h-4 rounded-full bg-glass-400" />
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-glass-100" />

                    {/* Analitik */}
                    <div className="flex items-start justify-between gap-6">
                        <div>
                            <p className="text-sm font-medium text-glass-800">Analitik Çerezler</p>
                            <p className="text-xs text-glass-500 mt-1 leading-relaxed">
                                Google Analytics ve Meta Pixel aracılığıyla kullanım
                                istatistikleri toplar. Yalnızca "Tümünü Kabul Et" ile etkinleşir.
                            </p>
                        </div>
                        <div className="flex-shrink-0 flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-glass-400 whitespace-nowrap">İsteğe bağlı</span>
                            <div className="w-9 h-5 rounded-full bg-glass-100 relative cursor-default">
                                <div className="absolute left-0.5 top-0.5 w-4 h-4 rounded-full bg-glass-300" />
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* Ana bar */}
            <div className="bg-white/95 backdrop-blur-md border-t border-glass-200 shadow-lg">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 space-y-3">

                    {/* Üst satır: metin + Tercihler */}
                    <div className="flex items-center justify-between gap-4">
                        <p className="text-xs text-glass-500 leading-relaxed flex-1">
                            Hizmet kalitesini ölçmek için analiz çerezleri kullanıyoruz.{' '}
                            <a
                                href="/privacy"
                                className="underline hover:text-glass-700 transition-colors"
                            >
                                Gizlilik Politikası
                            </a>
                        </p>
                        <button
                            onClick={() => setShowPreferences(v => !v)}
                            className="flex items-center gap-1 text-xs text-glass-400 hover:text-glass-600 transition-colors flex-shrink-0"
                        >
                            Tercihler
                            <svg
                                className={`w-3 h-3 transition-transform duration-500 ${
                                    showPreferences ? 'rotate-180' : 'rotate-0'
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M5 15l7-7 7 7"
                                />
                            </svg>
                        </button>
                    </div>

                    {/* Alt satır: iki buton yan yana tam genişlik */}
                    <div className="flex gap-2">
                        <button
                            onClick={acceptRequired}
                            className="flex-1 py-2 text-sm rounded-lg border border-glass-200 text-glass-500 hover:border-glass-300 hover:text-glass-700 transition-colors"
                        >
                            Zorunluları Kabul Et
                        </button>
                        <button
                            onClick={acceptAll}
                            className="flex-1 py-2 text-sm rounded-lg bg-glass-900 text-white hover:bg-glass-700 transition-colors font-medium"
                        >
                            Tümünü Kabul Et
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
}
